import { useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import LockIcon from '@material-ui/icons/Https';
import Button from 'components/CncUI/Button';
import Modal from 'components/CncUI/Modal';
import Radio from 'components/CncUI/Radio';
import Dropdown from 'components/CncUI/Dropdown';
import Typography from 'components/CncUI/Typography';
import Tooltip from 'components/CncUI/Tooltip';

import Loader from 'components/Loader';
import FeedbackButton from 'components/FeedbackButton';
import useStates from '../hooks/useStates';
import useError from '../hooks/useError';
import useCountry from 'hooks/useCountry';

const styles = ({ breakpoints, palette }) => ({
  root: {
    minWidth: 453,
    [breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  content: {
    [breakpoints.up('sm')]: {
      minWidth: 421,
      maxWidth: 421,
    },
  },
  group: {
    marginBottom: 16,
  },
  lockIcon: {
    marginBottom: -4,
    marginLeft: 2,
  },
  helpText: {
    color: palette.cncUI.primary.gray['+6'],
  },
  subtitle: {
    marginTop: -16,
    marginBottom: 16,
  },
});

const AddNewW4Dialog = ({ classes, onClose, onConfirm, open, ...props }) => {
  const onError = useError();
  const { country } = useCountry();
  const { data: states } = useStates({ onError, variables: { country } });
  const isCAN = country === 'CAN';
  const [w4Type, setW4Type] = useState(isCAN ? 'state' : '');
  const [stateId, setStateId] = useState('');
  const { federalLock } = props;
  const title = isCAN ? 'TD1' : 'W-4';

  // Clear selection on open
  useEffect(() => {
    if (open) {
      setW4Type(isCAN ? 'state' : '');
      setStateId('');
    }
  }, [open]);

  if (!states) return <Loader />;

  const isValid = w4Type === 'federal' || (w4Type === 'state' && !!stateId);

  const confirm = () =>
    onConfirm(w4Type === 'state' ? stateId : null, country).then(onClose);

  const lockMessage = `Please note that Cast & Crew is required to comply with
    IRS lock-in letters. If we receive an IRS lock-in letter for an employee,
    Cast & Crew is required to disregard the employee’s self-selected ${title}
    withholding and withhold according to IRS instruction. Employees should
    contact the IRS directly with any questions or concerns.`;

  const header = isCAN ? 'Select a province' : `Which ${title} are you adding?`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={classes.root}
      aria-labelledby="AddNewHeader"
    >
      <Modal.Title id="AddNewHeader">{header}</Modal.Title>
      <Modal.Content className={classes.content}>
        <Radio.Group
          value={w4Type}
          onChange={e => setW4Type(e.target.value)}
          className={classes.group}
        >
          {!isCAN && (
            <Grid container spacing={2}>
              <Grid item>
                <Radio
                  id="AddNewFederal"
                  disabled={federalLock}
                  value="federal"
                >
                  Federal {title}
                  {federalLock && (
                    <Tooltip size="medium" content={lockMessage}>
                      <LockIcon className={classes.lockIcon} />
                    </Tooltip>
                  )}
                </Radio>
              </Grid>
              <Grid item>
                <Radio id="AddNewState" value="state">
                  {isCAN ? 'Province TD1' : 'State W-4'}
                </Radio>
              </Grid>
            </Grid>
          )}
        </Radio.Group>
        {w4Type === 'state' && (
          <>
            {isCAN && (
              <Typography
                variant="body1"
                size="long"
                className={classes.subtitle}
              >
                A federal TD1 will be included to fill out with your provincial
                TD1.
              </Typography>
            )}
            <Dropdown
              fullWidth
              value={stateId}
              label={isCAN ? '' : 'For the state of'}
              placeholder={isCAN ? 'Select a province' : 'Select a state'}
              onChange={e => setStateId(e.target.value)}
              id="AddNewStateSelect"
              renderValue={value =>
                value ? states.find(s => s.id === value).name : ''
              }
            >
              {states.map(({ name, id }) => (
                <Dropdown.Item value={id} key={id}>
                  {name}
                </Dropdown.Item>
              ))}
            </Dropdown>
            <Typography variant="caption" italic className={classes.helpText}>
              If your required {isCAN ? 'province' : 'state'} is not listed, you
              cannot use MyCast&Crew to file your {isCAN ? 'province' : 'state'}{' '}
              {title} at this time. Please visit your{' '}
              {isCAN ? 'province' : 'state'}'s website to download the form and
              turn into your production.
            </Typography>
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Grid container spacing={2} justify="flex-end">
          <Grid item>
            <Button secondary id="AddNewCancelButton" onClick={onClose}>
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <FeedbackButton
              id="AddNewContinueButton"
              onClick={confirm}
              disabled={!isValid}
              errorMessage={`There was an error creating your ${title}.
                Please try again later or contact product support if the
                issue persists.`}
            >
              Next
            </FeedbackButton>
          </Grid>
        </Grid>
      </Modal.Actions>
    </Modal>
  );
};

export default withStyles(styles)(AddNewW4Dialog);
